import * as THREE from 'three'
import { Mob } from './Mob'
import Terrain from '../terrain'
import { setBoxUVs, buildMinecraftUVs } from './utils'
import { InventoryItemType, ItemType } from '../items'

const textureLoader = new THREE.TextureLoader()
const px = 0.0625 // 1/16 of a block

export class Cow extends Mob {
  legFL: THREE.Mesh
  legFR: THREE.Mesh
  legBL: THREE.Mesh
  legBR: THREE.Mesh
  head: THREE.Mesh

  animationPhase: number = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3, texturePath: string) {
    super(scene, terrain, position)
    this.speed = 1.0 
    
    const texture = textureLoader.load(texturePath)
    texture.magFilter = THREE.NearestFilter
    texture.minFilter = THREE.NearestFilter
    
    // Create standard material with roughness
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      side: THREE.FrontSide,
      roughness: 0.9,
      metalness: 0.0
    })
    
    // Head: 8x8x8
    const headGeo = new THREE.BoxGeometry(px*8, px*8, px*8)
    setBoxUVs(headGeo, buildMinecraftUVs(0, 0, 8, 8, 8))
    this.head = new THREE.Mesh(headGeo, material)
    // Head pivot is at base of head
    headGeo.translate(0, px*4, 0)
    // Positioning head
    this.head.position.set(0, px*16, px*6)
    this.mesh.add(this.head)
    this.headPart = this.head

    // Body: 10w x 16l x 8h
    const bodyGeo = new THREE.BoxGeometry(px*10, px*16, px*8)
    setBoxUVs(bodyGeo, buildMinecraftUVs(18, 14, 10, 16, 8))
    const body = new THREE.Mesh(bodyGeo, material)
    // Minecraft cow body is rotated 90 degrees around X to lay flat
    body.rotation.x = Math.PI / 2
    body.position.set(0, px*14, 0)
    this.mesh.add(body)

    // Legs: 4w x 12h x 4d each
    const legGeo = new THREE.BoxGeometry(px*4, px*12, px*4)
    setBoxUVs(legGeo, buildMinecraftUVs(0, 16, 4, 12, 4))
    // Shift geometry so pivot is at top of leg
    legGeo.translate(0, -px*6, 0)
    
    this.legFL = new THREE.Mesh(legGeo, material)
    this.legFR = new THREE.Mesh(legGeo, material)
    this.legBL = new THREE.Mesh(legGeo, material)
    this.legBR = new THREE.Mesh(legGeo, material)
    
    // Position legs relative to body under it
    this.legFL.position.set(px*3, px*12, px*5)
    this.legFR.position.set(-px*3, px*12, px*5)
    this.legBL.position.set(px*3, px*12, -px*5)
    this.legBR.position.set(-px*3, px*12, -px*5)

    this.mesh.add(this.legFL, this.legFR, this.legBL, this.legBR)

    // Ensure the entire cow receives/casts shadows
    this.mesh.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.castShadow = true
        object.receiveShadow = true
      }
    })
  }

  updateAnimation(delta: number): void {
    if (this.state === 'walking') {
      this.animationPhase += delta * 8
      const swing = Math.sin(this.animationPhase) * 0.6
      this.legFL.rotation.x = swing
      this.legBL.rotation.x = -swing
      this.legFR.rotation.x = -swing
      this.legBR.rotation.x = swing
      
      this.head.rotation.x = Math.sin(this.animationPhase * 2) * 0.05
      // head yaw belongs to the look-at hook while it's tracking the player
      if (!this.headLookActive) {
        this.head.rotation.y = Math.sin(this.animationPhase * 0.5) * 0.1
      }
    } else {
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, delta*5)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, delta*5)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, delta*5)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, delta*5)
      
      this.head.rotation.x = THREE.MathUtils.lerp(this.head.rotation.x, 0, delta*5)
      if (!this.headLookActive) {
        this.head.rotation.y = THREE.MathUtils.lerp(this.head.rotation.y, 0, delta*5)
      }
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    const beefCount = 1 + Math.floor(Math.random() * 3)
    for (let i = 0; i < beefCount; i++) drops.push(ItemType.rawBeef)
    if (Math.random() < 0.8) drops.push(ItemType.leather)
    return drops
  }
}
